import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { apiClient } from '../api';
import i18n from '../i18n';

type ThemeMode = 'system' | 'light' | 'dark';

const isThemeMode = (value: unknown): value is ThemeMode =>
  value === 'system' || value === 'light' || value === 'dark';

const DEBUG_KEY = 'debug-theme';

interface ThemeContextType {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  appliedTheme: 'light' | 'dark';
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [themeMode, setThemeModeState] = useState<ThemeMode>('system');
  const [appliedTheme, setAppliedTheme] = useState<'light' | 'dark'>('light');
  const [isLoading, setIsLoading] = useState(false);

  // Get user's theme preference from user data
  useEffect(() => {
    if (user) {
      const userTheme = (user as any).theme_preference as ThemeMode | undefined;
      if (userTheme && ['system', 'light', 'dark'].includes(userTheme)) {
        setThemeModeState(userTheme);
      }
      const preferredLanguage = (user as any).preferred_language as string | undefined;
      if (
        preferredLanguage &&
        preferredLanguage !== 'auto' &&
        i18n.language !== preferredLanguage
      ) {
        i18n.changeLanguage(preferredLanguage).catch(error => {
          console.error('Failed to apply user language preference:', error);
        });
      }
    }
  }, [user]);

  // Apply theme based on mode
  useEffect(() => {
    const applyTheme = () => {
      let finalTheme: 'light' | 'dark' = 'light';

      const debugOverride = localStorage.getItem(DEBUG_KEY);
      if (isThemeMode(debugOverride)) {
        finalTheme = debugOverride === 'system' ? finalTheme : debugOverride;
      }

      if (!debugOverride || debugOverride === 'system') {
        if (themeMode === 'system') {
          if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
            finalTheme = 'dark';
          }
          if (
            typeof window !== 'undefined' &&
            (window as any).Telegram?.WebApp?.colorScheme
          ) {
            finalTheme = (window as any).Telegram.WebApp.colorScheme as 'light' | 'dark';
          }
        } else {
          finalTheme = themeMode;
        }
      }

      setAppliedTheme(finalTheme);

      // Apply theme to document
      if (finalTheme === 'dark') {
        document.documentElement.setAttribute('data-theme', 'dark');
      } else {
        document.documentElement.removeAttribute('data-theme');
      }
    };

    applyTheme();

    (window as any).__setDebugTheme = (mode: ThemeMode) => {
      if (isThemeMode(mode)) {
        localStorage.setItem(DEBUG_KEY, mode);
        applyTheme();
      }
    };
    (window as any).__clearDebugTheme = () => {
      localStorage.removeItem(DEBUG_KEY);
      applyTheme();
    };
    (window as any).__toggleDebugTheme = () => {
      const current = localStorage.getItem(DEBUG_KEY) as ThemeMode | null;
      const next = current === 'dark' ? 'light' : 'dark';
      localStorage.setItem(DEBUG_KEY, next);
      applyTheme();
    };

    const handleStorage = (e: StorageEvent) => {
      if (e.key === DEBUG_KEY) applyTheme();
    };
    window.addEventListener('storage', handleStorage);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (themeMode === 'system') {
        applyTheme();
      }
    };

    mediaQuery.addEventListener('change', handleChange);

    // Listen for Telegram theme changes
    const telegramWebApp = (window as any).Telegram?.WebApp;
    if (telegramWebApp && themeMode === 'system') {
      const handleTelegramThemeChange = () => {
        applyTheme();
      };
      telegramWebApp.onEvent('themeChanged', handleTelegramThemeChange);

      return () => {
        mediaQuery.removeEventListener('change', handleChange);
        telegramWebApp.offEvent('themeChanged', handleTelegramThemeChange);
      };
    }

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, [themeMode]);

  const setThemeMode = async (mode: ThemeMode) => {
    setIsLoading(true);
    const previousMode = themeMode;
    setThemeModeState(mode);
    localStorage.setItem(DEBUG_KEY, mode);

    try {
      const response = await apiClient.post('/v2/set_theme', {
        theme_preference: mode,
      });
      const serverMode = response?.theme_preference;
      if (isThemeMode(serverMode)) {
        setThemeModeState(serverMode);
      }
    } catch (error) {
      console.error('Failed to save theme preference:', error);
      setThemeModeState(previousMode);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeContext.Provider value={{ themeMode, setThemeMode, appliedTheme, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
